import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import app from '@/firebase';
import { createFunctions, ensureFunctionsEmulator } from '@/utils/firebase/emulator';
import { logger } from '@/utils/logger';

/**
 * The backend-composed "Today's Session" (aswb-backend `getTodaysSession` /
 * `startTodaysSession`). The engine decides the plan, the size, the focus
 * category and the difficulty; the client renders what it is told and taps
 * start. `startTodaysSession` recomputes the same plan server-side and
 * materializes it through the normal session path, so its response is an
 * ordinary session the quiz screens already know how to run.
 */

export type TodaysSessionState =
  | 'ready'
  | 'resume'
  | 'needs-exam-date'
  | 'metered'
  | 'capped'
  | 'passed';

export interface TodaysSessionPlan {
  reviewCount: number;
  totalDue: number;
  adaptiveCount: number;
  focusCategory: string;
  why: string;
  estimatedMinutes: number;
  testType: string;
}

export interface TodaysSessionReadiness {
  score: number;
  deltaThisWeek: number | null;
  /** Hard countdown, present only for a booked exam date. */
  daysToExam: number | null;
  /** Soft target window ('2026-Q4') when the exam is approved but not booked. */
  targetWindow?: string | null;
  source: 'diagnostic' | 'adaptive';
}

export interface TodaysSession {
  state: TodaysSessionState;
  resumeSessionId?: string;
  plan?: TodaysSessionPlan;
  readiness?: TodaysSessionReadiness;
}

export const todaysSessionKeys = {
  all: ['todays-session'] as const,
};

function callable<Req extends object, Res>(name: string) {
  const functions = createFunctions(app);
  try {
    ensureFunctionsEmulator(functions);
  } catch {
    // Emulator wiring is best-effort; the callable still resolves against prod.
  }
  return httpsCallable<Req, Res>(functions, name);
}

function isTodaysSession(value: unknown): value is TodaysSession {
  const state = (value as TodaysSession | null)?.state;
  return (
    typeof state === 'string' &&
    ['ready', 'resume', 'needs-exam-date', 'metered', 'capped', 'passed'].includes(state)
  );
}

/**
 * Today's plan, or `undefined` while loading and whenever the callable fails
 * or answers something this client does not understand. Callers render their
 * own fallback for `undefined` rather than guessing at a plan.
 */
export function useTodaysSession() {
  return useQuery({
    queryKey: todaysSessionKeys.all,
    queryFn: async (): Promise<TodaysSession | undefined> => {
      const result = await callable<Record<string, never>, TodaysSession>('getTodaysSession')({});
      if (!isTodaysSession(result.data)) {
        logger.warn('useTodaysSession', 'Unrecognized getTodaysSession payload', {
          data: result.data,
        });
        return undefined;
      }
      return result.data;
    },
    // The plan turns over on the day, on a finished session, and on a due-review
    // change — short enough to stay honest, long enough to survive tab-hopping.
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/** Materializes today's plan and hands back the session to navigate into. */
export function useStartTodaysSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<string> => {
      const result = await callable<Record<string, never>, { sessionId?: string }>(
        'startTodaysSession',
      )({});
      const sessionId = result.data?.sessionId;
      if (!sessionId) throw new Error('startTodaysSession returned no sessionId');
      return sessionId;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: todaysSessionKeys.all });
    },
  });
}
